import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { PaginatedResponse, PressReleaseLight } from '@finance-lab/utils/types'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { PressReleaseApi } from '../../api/PressReleasesApi'
import { GridColDef } from '@mui/x-data-grid/models'
import { formatDateYYYYMMDD, formatDateYYYYMMDDhhmmss } from '../../packages/utils/numberFormatters'
import { PressReleaseAvatar } from '../../components/entities/PressReleaseAvatar'
import { PressReleaseRelatedTransactions } from '../../components/entities/PressReleaseRelatedTransactions'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Box from '@mui/material/Box'
import { GridApiPro } from '@mui/x-data-grid-pro/models'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Grid from '@mui/system/Grid'
import { SelectDateRange } from '../components/SelectDateRange'
import useFetchEntitiesWithCache from '../../hooks/useFetchEntitiesWithCache'
import { useQuery } from '../../hooks/useQuery'
import { Link, useNavigate } from 'react-router-dom'
import { getNewsLink } from '../../utils/get-news-link'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import { flagPath } from '../../components/entities/CompaniesGrid'

const pressReleaseApi = new PressReleaseApi()

export const PressReleasesView = () => {
    const query = useQuery()
    const navigate = useNavigate()
    const [pageInfo, setPageInfo] = useState<PaginatedResponse<Omit<PressReleaseLight, 'body'>>>({
        totalCount: 0,
        data: [],
    })
    const [showOnlyReports, setShowOnlyReports] = useState(false)
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 30,
    })
    const [showOnlyWithRelatedTransactions, setShowOnlyWithRelatedTransactions] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [preliminarySearchValue, setPreliminarySearchValue] = useState('')
    const apiRef = useRef<GridApiPro | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const minDate = query.get('startDate')
    const maxDate = query.get('endDate')
    const { isLoading: isLoadingCompanies, result: companies } = useFetchEntitiesWithCache('company')
    const selectedCompany = companies?.find((c) => c.id === query.get('companyId'))?.id ?? 'all'

    const handleSelectCompany = (companyId?: string) => {
        if (companyId && companyId !== 'all') {
            query.set('companyId', companyId)
        } else {
            query.delete('companyId')
        }
        navigate(`?${query.toString()}`, { replace: true })
    }

    const handleSelectDate = ([startDate, endDate]: [Date | undefined, Date | undefined]) => {
        if (startDate) {
            query.set('startDate', formatDateYYYYMMDD(startDate, true))
        } else {
            query.delete('startDate')
        }

        if (endDate) {
            query.set('endDate', formatDateYYYYMMDD(endDate, true))
        } else {
            query.delete('endDate')
        }

        navigate(`?${query.toString()}`, { replace: true })
    }

    useEffect(() => {
        const fetchData = async () => {
            const { data, totalCount } = await pressReleaseApi.allPressReleases(paginationModel, {
                companyId: selectedCompany === 'all' ? undefined : selectedCompany,
                maxPublicationDate: maxDate ?? undefined,
                minPublicationDate: minDate ?? undefined,
                showOnlyReports,
                showOnlyWithRelatedTransactions,
                search: searchValue,
            })

            setPageInfo({ data: data.map(({ body, ...pr }) => pr), totalCount })
            setIsLoading(false)

            await Promise.resolve()
        }

        if (isLoadingCompanies) return

        void fetchData()
        setIsLoading(true)
    }, [
        isLoadingCompanies,
        maxDate,
        minDate,
        paginationModel,
        searchValue,
        selectedCompany,
        showOnlyReports,
        showOnlyWithRelatedTransactions,
    ])

    const columns: GridColDef<Omit<PressReleaseLight, 'body'>>[] = [
        {
            field: 'avatar',
            display: 'flex',
            align: 'left',
            sortable: false,
            width: 154,
            headerName: '',
            renderCell: ({ row }) => {
                const langs = [
                    { prId: row.id, language: row.language },
                    ...row.translations,
                ] as PressReleaseLight['translations']

                return (
                    <>
                        <PressReleaseAvatar pressRelease={row} size={'small'} />
                        {langs
                            .sort((a, b) => a.language.localeCompare(b.language))
                            .map((lg) => (
                                <Link key={lg.prId} to={getNewsLink(row.companyId, lg.prId)}>
                                    <img
                                        src={flagPath(
                                            lg.language === 'en' ? 'gb' : lg.language == 'sv' ? 'se' : lg.language,
                                        )}
                                        width={25}
                                        style={{ marginLeft: 14 }}
                                    />
                                </Link>
                            ))}
                    </>
                )
            },
        },
        {
            field: 'relatedTransactions',
            display: 'flex',
            align: 'left',
            sortable: false,
            headerName: '',
            renderCell: ({ row }) => {
                return <PressReleaseRelatedTransactions relatedTransactions={row.relatedTransactions} />
            },
        },
        {
            headerName: 'Publiceringsdatum',
            field: 'publishedAt',
            type: 'dateTime',
            minWidth: 160,
            valueFormatter: (v) => formatDateYYYYMMDDhhmmss(v),
        },
        {
            headerName: 'Rubrik',
            field: 'title',
            minWidth: 360,
            flex: 1,
        },
    ]
    const companiesItems = [
        { id: 'all', label: 'Visa alla' },
        ...(companies?.map((c) => ({ id: c.id, label: c.name })) ?? []),
    ]

    return (
        <>
            <Helmet title='Pressmeddelanden' />
            <Typography variant='h3' gutterBottom display='block'>
                Pressmeddelanden
            </Typography>

            <Grid container>
                <Grid size={6}>
                    <Box>
                        <FormControlLabel
                            control={<Switch onChange={() => setShowOnlyReports((v) => !v)} />}
                            label='Visa enbart rapporter'
                            value={showOnlyReports}
                        />
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={<Switch onChange={() => setShowOnlyWithRelatedTransactions((v) => !v)} />}
                            label='Visa enbart PM med relaterade transaktioner'
                            value={showOnlyWithRelatedTransactions}
                        />
                    </Box>
                </Grid>

                <Grid size={6}>
                    <Box>
                        <TextField
                            label='Fritextsökning'
                            size='small'
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') setSearchValue(preliminarySearchValue)
                            }}
                            onChange={(event) => setPreliminarySearchValue(event.target.value)}
                            value={preliminarySearchValue}
                        />
                        <Button
                            variant='outlined'
                            size='medium'
                            sx={{ marginLeft: 2 }}
                            onClick={() => setSearchValue(preliminarySearchValue)}
                        >
                            Sök
                        </Button>
                    </Box>

                    <Box sx={{ marginY: 4 }}>
                        <SelectDateRange
                            granularity='day'
                            onChange={handleSelectDate}
                            size='small'
                            values={[minDate ? new Date(minDate) : undefined, maxDate ? new Date(maxDate) : undefined]}
                        />
                    </Box>

                    <Box sx={{ marginY: 4 }}>
                        {!isLoadingCompanies && (
                            <Autocomplete
                                clearOnEscape
                                options={companiesItems}
                                renderInput={(params) => <TextField {...params} label='Bolag' />}
                                renderValue={(value, getItemProps) =>
                                    value.id === 'all' ? '' : <Chip label={value.label} {...getItemProps()} />
                                }
                                noOptionsText='Inget bolag hittades'
                                onChange={(e, v) => handleSelectCompany(v?.id)}
                                value={companiesItems?.find((c) => c.id === selectedCompany)}
                            />
                        )}
                    </Box>
                </Grid>
            </Grid>

            <Divider />

            <DataGridPro
                autosizeOnMount
                autosizeOptions={{
                    columns: ['relatedTransactions', 'avatar'],
                }}
                apiRef={apiRef}
                columns={columns}
                filterMode='server'
                loading={isLoading}
                onPaginationModelChange={setPaginationModel}
                onCellClick={(params, event) => {
                    if (params.colDef.field === 'relatedTransactions' || params.colDef.field === 'avatar') return

                    const link = getNewsLink(params.row.companyId, params.row.id)

                    if (event.ctrlKey) {
                        window.open(link, '_blank')
                    } else {
                        navigate(link)
                    }
                }}
                pagination
                paginationModel={paginationModel}
                paginationMode='server'
                rows={pageInfo.data}
                rowCount={pageInfo.totalCount}
                sx={{
                    '.MuiDataGrid-row': {
                        cursor: 'pointer',
                        ':hover': {},
                    },
                }}
            />
        </>
    )
}
